import axios from 'axios';
import {
    Address, BarteringDocument, BsListing,
    BsUserLoggedIn, BsUserRegistration, ExtendedBarteringDocument, Feedback,
    ItemClassifications, Listing, ProductTag, WithStringId, WorkOrder,
    Notification, Variables
} from './types';


export class BApi {
    constructor() {
    }

    async me(): Promise<WithStringId<BsUserLoggedIn>> {
        return axios.get('/api/me/details')
            .then(res => {
                return res.data;
            })
            .catch(err => {
                throw err.response.data;
            })
    }
    uploadFiles(files: FileList | null, onProgress: (percent: number) => void, onFiles: (files: Array<string>) => void, onDone: () => void) {
        if (!files) return;
        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('file', files[i])
        }

        axios.post('/api/file', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: progress => {
                if (progress.loaded !== progress.total) {
                    onProgress(Math.trunc((progress.loaded / progress.total) * 100))
                }
            }
        })
            .then(response => {
                onFiles([...response.data.submitted])
            })
            .finally(() => onDone());
    }
    async updateAccount(firstName: string, lastName: string, birthday: number): Promise<void> {
        return axios.post('/api/me/update', { firstName, lastName, birthday })
            .then(res => res.data)
            .catch(err => {
                throw err.response.data;
            })  
    }
    async postListing(tags: Array<string>, price: number, images: Array<string>, primaryImage: number) {
        return axios.post('/api/listing', { tagsAliases: tags, price, images, primaryImage })
            .then(res => res.data as string)
            .catch(err => {
                throw err.response.data;
            })
    }
    async activeBarters(): Promise<Array<WithStringId<BarteringDocument>>> {
        return axios.get('/api/me/bartering/active')
            .then(res => res.data as Array<WithStringId<BarteringDocument>>)
            .catch(err => {
                throw err.response.data;
            })

    }
    async allBarters(): Promise<Array<WithStringId<BarteringDocument>>> {
        return axios.get('/api/me/bartering/all')
            .then(res => res.data as Array<WithStringId<BarteringDocument>>)
            .catch(err => {
                throw err.response.data;
            })

    }
    async markInterested(id: string): Promise<void> {
        return axios.post('/api/listing/' + id + '/interested')
            .then(req => {

            })
            .catch(err => {
                throw err.response.data;
            })
    }
    async markUninterested(id: string): Promise<void> {
        return axios.post('/api/listing/' + id + '/uninterested')
            .then(req => {

            })
            .catch(err => {
                throw err.response.data;
            })
    }
    async counterOffer(barterId: string, items?: Array<string>, price?: number): Promise<void> {
        return axios.post('/api/barters/' + barterId + '/counter', { items, price })
            .then(req => {

            })
            .catch(err => {
                throw err.response.data;
            })
    }
    async acceptTrade(barterId: string): Promise<void> {
        return axios.post('/api/barters/' + barterId + '/accept')
            .then(req => {

            })
            .catch(err => {
                throw err.response.data;
            })
    }
    async declineTrade(barterId: string): Promise<void> {
        return axios.post('/api/barters/' + barterId + '/decline')
            .then(req => {

            })
            .catch(err => {
                throw err.response.data;
            })
    }
    async buyerInventory(barterId: string): Promise<Array<WithStringId<Listing>>> {
        return axios.get('/api/barters/' + barterId + '/buyerinventory')
            .then(res => res.data)
    }
    async buyerInventories(barterIds: Array<string>): Promise<Array<Array<WithStringId<Listing>>>> {
        return Promise.all(barterIds.map(barterId => axios.get('/api/barters/' + barterId + '/buyerinventory')))
            .then(inventories => inventories.map(res => res.data))
    }
    async tradecenter(): Promise<Array<ExtendedBarteringDocument>> {
        return axios.get('/api/me/bartering/tradecenter')
            .then(res => res.data)
    }
    async logout(): Promise<void> {
        return axios.get('/api/logout')
            .then(() => { })
            .catch(err => {
                throw err.response.data;
            })
    }
    async register(user: BsUserRegistration) {
        // delete all below
        const { firstName, lastName, email, password, birthday } = user;
        const username = null as any as string;
        const nextUser: BsUserRegistration = { firstName, lastName, email, password, birthday, username, readyToTrade: false }
        return axios.post('/api/register', nextUser).then(res => {
            if (res.status !== 200) {
                throw res.data;
            }
        })
            .catch(err => {
                throw err.response.data;
            })
    }
    async login(email: string, password: string, rememberMe: boolean): Promise<WithStringId<BsUserLoggedIn>> {
        //delete all below
        return axios.post('/api/login', { email, password, rememberMe })
            .then(() => this.me())
            .catch(err => {
                throw err.response.data
            });
    }
    async getClassifications(): Promise<ItemClassifications> {
        return axios.get('/api/meta/item_classifications')
            .then(res => res.data);
    }
    async submitFeedback(type: Feedback['type'], message: string): Promise<void> {
        return axios.post('/api/feedback', { type, message })
            .then(res => res.data)
    }
    async getCategoryListings(category: string, limit: number, page: number): Promise<Array<Listing>> {
        return axios.get('/api/listings/categories/' + category + '?limit=' + limit + '&page=' + page)
            .then(res => res.data);
    }
    async getLatestListings(limit: number, page: number): Promise<{ pages: number, listings: Array<WithStringId<Listing>> }> {
        return axios.get('/api/listings/latest?limit=' + limit + '&page=' + page)
            .then(res => res.data);
    }
    async getPersonalListings(limit: number, page: number): Promise<{ pages: number, listings: Array<WithStringId<Listing>> }> {
        return axios.get('/api/listings/personal?limit=' + limit + '&page=' + page)
            .then(res => res.data);
    }
    async getTagListings(tags: Array<string>, limit: number, page: number): Promise<{ pages: number, listings: Array<WithStringId<Listing>> }> {
        return axios.post('/api/listings/tags?limit=' + limit + '&page=' + page, tags)
            .then(res => res.data);
    }
    async getListing(itemId: string): Promise<WithStringId<Listing>> {
        return axios.get('/api/listing?id=' + itemId)
            .then(res => res.data)
    }
    async getListings(ids: Array<string>): Promise<Array<WithStringId<Listing>>> {
        return axios.post('/api/listings/multiple', ids)
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async getTags(): Promise<Array<ProductTag>> {
        return axios.get('/api/tags')
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async getRoomTags(): Promise<Array<ProductTag>> { 
        return axios.get('/api/tags/room')
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async search(query: string): Promise<Array<WithStringId<Listing>>> {
        return axios.get('/api/search?q=' + query)
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async updateAddresses(addresses: Array<Address>) {
        return axios.post('/api/me/updateaddresses', { addresses })
            .then(res => res.data)
            .catch(err => {
                throw err.response.data;
            })
    }
    async getAddresses(): Promise<Array<Address>> {
        return axios.get('/api/me/addresses')
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async getNotifications(): Promise<Array<WithStringId<Notification<string>>>> {
        return axios.get('/api/me/notifications')
            .then(res => res.data)
            .catch(err => {
                throw err.response.data
            })
    }
    async getLandingAll(): Promise<Variables> {
        return axios.get('/api/landing/all')
            .then(res => res.data)
            .catch(err => {
                throw err.response.data;
            })
    }
    getLandingUrl(index: string): string{
        return '/api/landing/images/' + index;
    }
}

class WorkOrders extends BApi {
    async getOrders(pageSize: number, page: number): Promise<{ pages: number, orders: Array<WithStringId<WorkOrder>> }> {
        return axios.get('/api/barters/orders/?pageSize=' + pageSize + '&page=' + page)
            .then(res => res.data)
            .catch(err => {
                throw err.response.data;
            })
    }
    async setAddress(orderId: string, address: Address) {
        return axios.post('/api/barters/orders/' + orderId + '/setaddress', { address })
            .then(res => res.data)
            .catch(err => {
                throw err.response.data;
            })
    }
}

export default new WorkOrders();